import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full" }
const _hoisted_2 = { class: "border-b px-4 py-2.5 flex items-center justify-between shrink-0" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center gap-1"
}
const _hoisted_5 = {
  key: 0,
  class: "flex-1 flex items-center justify-center p-8"
}
const _hoisted_6 = { class: "text-center space-y-3" }
const _hoisted_7 = { class: "p-3 border-b space-y-2 shrink-0" }
const _hoisted_8 = { class: "flex gap-2" }
const _hoisted_9 = {
  key: 0,
  class: "flex flex-wrap gap-1"
}
const _hoisted_10 = {
  key: 0,
  class: "p-0"
}
const _hoisted_11 = {
  key: 1,
  class: "p-4"
}
const _hoisted_12 = { class: "border-t p-3 space-y-2 shrink-0" }
const _hoisted_13 = { class: "flex gap-2" }
const _hoisted_14 = { class: "flex gap-2" }
const _hoisted_15 = { class: "space-y-4 py-4" }
const _hoisted_16 = { class: "space-y-2" }
const _hoisted_17 = { class: "space-y-2" }
const _hoisted_18 = { class: "space-y-2" }
const _hoisted_19 = {
  key: 0,
  class: "bg-muted rounded-md p-3"
}
const _hoisted_20 = { class: "text-xs text-muted-foreground" }

import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import {
  FileOutput,
  Copy,
  Trash2,
  Save,
  Loader2,
  Eye,
  Pencil,
  Download,
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MdPreview, MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import { useCopilotStore } from '../store'
import { toast } from 'vue-sonner'
import { useMarkdownPdf } from '@/composables/useMarkdownPdf'
import type { DocumentType, DocumentEntityType } from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'OutputPanel',
  setup(__props) {

const copilotStore = useCopilotStore()
const { projectContext } = storeToRefs(copilotStore)

// Local editable state
const editTitle = ref('')
const editContent = ref('')
const editDocType = ref<DocumentType>('other')
const editTags = ref<string[]>([])
const isEditing = ref(true)
const showSaveDialog = ref(false)
const isSaving = ref(false)
const saveEntityType = ref<DocumentEntityType>('project')

// PDF export
const pdfFilename = computed(() => editTitle.value || 'documento')
const { isGenerating: isGeneratingPdf, downloadPdf } = useMarkdownPdf(editContent, pdfFilename)

const hasDocument = computed(() => !!editContent.value)

const documentTypeOptions: Array<{ value: DocumentType; label: string }> = [
  { value: 'scope', label: 'Alcance' },
  { value: 'architecture', label: 'Arquitectura' },
  { value: 'specifications', label: 'Especificaciones' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Otro' },
]

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(editContent.value)
    toast.success('Contenido copiado al portapapeles')
  } catch {
    toast.error('Error al copiar')
  }
}

const handleDiscard = () => {
  editTitle.value = ''
  editContent.value = ''
  editDocType.value = 'other'
  editTags.value = []
  isEditing.value = true
}

const handleSave = async () => {
  if (!projectContext.value || !editTitle.value || !editContent.value) return

  isSaving.value = true
  try {
    const doc = {
      title: editTitle.value,
      content: editContent.value,
      documentType: editDocType.value,
      suggestedTags: editTags.value,
    }

    const entityId = projectContext.value.projectId

    await copilotStore.saveGeneratedDocument(doc, saveEntityType.value, entityId)
    toast.success('Documento guardado exitosamente')
    showSaveDialog.value = false
    handleDiscard()
  } catch (error) {
    console.error('Error saving document:', error)
    toast.error('Error al guardar el documento')
  } finally {
    isSaving.value = false
  }
}

const removeTag = (index: number) => {
  editTags.value.splice(index, 1)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(FileOutput), { class: "h-4 w-4" }),
        _cache[11] || (_cache[11] = _createElementVNode("span", { class: "text-sm font-medium" }, "Output", -1))
      ]),
      (hasDocument.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(_unref(Button), {
              variant: "ghost",
              size: "icon",
              class: "h-7 w-7",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (isEditing.value = !isEditing.value))
            }, {
              default: _withCtx(() => [
                (isEditing.value)
                  ? (_openBlock(), _createBlock(_unref(Eye), {
                      key: 0,
                      class: "h-3.5 w-3.5"
                    }))
                  : (_openBlock(), _createBlock(_unref(Pencil), {
                      key: 1,
                      class: "h-3.5 w-3.5"
                    }))
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    (!hasDocument.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(FileOutput), { class: "h-12 w-12 mx-auto text-muted-foreground/40" }),
            _cache[13] || (_cache[13] = _createElementVNode("div", null, [
              _createElementVNode("p", { class: "text-sm font-medium" }, "Panel de Output"),
              _createElementVNode("p", { class: "text-xs text-muted-foreground mt-1" }, " Pega contenido del chat aqui para editarlo y guardarlo como documento ")
            ], -1)),
            _createVNode(_unref(Button), {
              variant: "outline",
              size: "sm",
              class: "text-xs",
              onClick: _cache[1] || (_cache[1] = ($event: any) => {isEditing.value = true; editContent.value = ' '})
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Pencil), { class: "h-3 w-3 mr-1" }),
                _cache[12] || (_cache[12] = _createTextVNode(" Comenzar a escribir ", -1))
              ]),
              _: 1
            })
          ])
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(Input), {
              modelValue: editTitle.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editTitle).value = $event)),
              placeholder: "Titulo del documento",
              class: "text-sm font-medium h-8"
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(Select), {
                modelValue: editDocType.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((editDocType).value = $event))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SelectTrigger), { class: "h-7 text-xs flex-1" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SelectValue), { placeholder: "Tipo" })
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SelectContent), { class: "z-[200]" }, {
                    default: _withCtx(() => [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(documentTypeOptions, (opt) => {
                        return _createVNode(_unref(SelectItem), {
                          key: opt.value,
                          value: opt.value
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(opt.label), 1)
                          ]),
                          _: 2
                        }, 1032, ["value"])
                      }), 64))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            (editTags.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editTags.value, (tag, idx) => {
                    return (_openBlock(), _createBlock(_unref(Badge), {
                      key: idx,
                      variant: "secondary",
                      class: "text-xs cursor-pointer",
                      onClick: ($event: any) => (removeTag(idx))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(tag) + " × ", 1)
                      ]),
                      _: 2
                    }, 1032, ["onClick"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]),
          _createVNode(_unref(ScrollArea), { class: "flex-1" }, {
            default: _withCtx(() => [
              (isEditing.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createVNode(_unref(MdEditor), {
                      modelValue: editContent.value,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editContent).value = $event)),
                      language: "en-US",
                      preview: false,
                      toolbars: ['bold', 'italic', 'strikeThrough', '-', 'title', 'sub', 'sup', '-', 'unorderedList', 'orderedList', 'task', '-', 'codeRow', 'code', 'link', 'table', '-', 'revoke', 'next'],
                      class: "copilot-editor",
                      style: {"height":"100%"}
                    }, null, 8, ["modelValue"])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createVNode(_unref(MdPreview), {
                      "model-value": editContent.value,
                      language: "en-US",
                      class: "copilot-output-preview"
                    }, null, 8, ["model-value"])
                  ]))
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              _createVNode(_unref(Button), {
                class: "flex-1 text-xs h-8",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (showSaveDialog.value = true)),
                disabled: !editTitle.value || !editContent.value
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Save), { class: "h-3 w-3 mr-1" }),
                  _cache[14] || (_cache[14] = _createTextVNode(" Guardar como Documento ", -1))
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createVNode(_unref(Button), {
                variant: "outline",
                size: "sm",
                class: "flex-1 text-xs h-7",
                onClick: _unref(downloadPdf),
                disabled: isEditing.value || _unref(isGeneratingPdf)
              }, {
                default: _withCtx(() => [
                  (_unref(isGeneratingPdf))
                    ? (_openBlock(), _createBlock(_unref(Loader2), {
                        key: 0,
                        class: "h-3 w-3 mr-1 animate-spin"
                      }))
                    : (_openBlock(), _createBlock(_unref(Download), {
                        key: 1,
                        class: "h-3 w-3 mr-1"
                      })),
                  _cache[15] || (_cache[15] = _createTextVNode(" PDF ", -1))
                ]),
                _: 1
              }, 8, ["onClick", "disabled"]),
              _createVNode(_unref(Button), {
                variant: "outline",
                size: "sm",
                class: "flex-1 text-xs h-7",
                onClick: handleCopy
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Copy), { class: "h-3 w-3 mr-1" }),
                  _cache[16] || (_cache[16] = _createTextVNode(" Copiar ", -1))
                ]),
                _: 1
              }),
              _createVNode(_unref(Button), {
                variant: "outline",
                size: "sm",
                class: "flex-1 text-xs h-7",
                onClick: handleDiscard
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Trash2), { class: "h-3 w-3 mr-1" }),
                  _cache[17] || (_cache[17] = _createTextVNode(" Descartar ", -1))
                ]),
                _: 1
              })
            ])
          ])
        ], 64)),
    _createVNode(_unref(Dialog), {
      open: showSaveDialog.value,
      "onUpdate:open": _cache[10] || (_cache[10] = ($event: any) => ((showSaveDialog).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DialogContent), { class: "z-[200]" }, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogHeader), null, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogTitle), null, {
                  default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                    _createTextVNode("Guardar Documento", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(DialogDescription), null, {
                  default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                    _createTextVNode(" El documento se guardara en el proyecto seleccionado. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("div", _hoisted_16, [
                _cache[20] || (_cache[20] = _createElementVNode("label", { class: "text-sm font-medium" }, "Titulo", -1)),
                _createVNode(_unref(Input), {
                  modelValue: editTitle.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editTitle).value = $event)),
                  class: "text-sm"
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_17, [
                _cache[21] || (_cache[21] = _createElementVNode("label", { class: "text-sm font-medium" }, "Tipo de Documento", -1)),
                _createVNode(_unref(Select), {
                  modelValue: editDocType.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editDocType).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(SelectTrigger), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(SelectValue), { placeholder: "Seleccionar tipo" })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(SelectContent), { class: "z-[200]" }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(documentTypeOptions, (opt) => {
                          return _createVNode(_unref(SelectItem), {
                            key: opt.value,
                            value: opt.value
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(opt.label), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"])
                        }), 64))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_18, [
                _cache[24] || (_cache[24] = _createElementVNode("label", { class: "text-sm font-medium" }, "Guardar en", -1)),
                _createVNode(_unref(Select), {
                  modelValue: saveEntityType.value,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((saveEntityType).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(SelectTrigger), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(SelectValue), { placeholder: "Seleccionar destino" })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(SelectContent), { class: "z-[200]" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(SelectItem), { value: "project" }, {
                          default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                            _createTextVNode("Proyecto", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(_unref(SelectItem), { value: "application" }, {
                          default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                            _createTextVNode("Aplicacion", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              (_unref(projectContext))
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createElementVNode("p", _hoisted_20, [
                      _cache[25] || (_cache[25] = _createTextVNode(" Se guardara en: ", -1)),
                      _createElementVNode("strong", null, _toDisplayString(_unref(projectContext).projectName), 1)
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createVNode(_unref(DialogFooter), null, {
              default: _withCtx(() => [
                _createVNode(_unref(Button), {
                  variant: "outline",
                  onClick: _cache[9] || (_cache[9] = ($event: any) => (showSaveDialog.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                    _createTextVNode(" Cancelar ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(Button), {
                  onClick: handleSave,
                  disabled: isSaving.value || !editTitle.value
                }, {
                  default: _withCtx(() => [
                    (isSaving.value)
                      ? (_openBlock(), _createBlock(_unref(Loader2), {
                          key: 0,
                          class: "h-4 w-4 mr-2 animate-spin"
                        }))
                      : _createCommentVNode("", true),
                    _cache[27] || (_cache[27] = _createTextVNode(" Guardar ", -1))
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"])
  ]))
}
}

})